/**
 * @fileoverview Componente responsável por renderizar o mapa de calor do dashboard.
 */

import React, { useEffect, useState, useMemo } from "react";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import {
  Box,
  Typography,
  CircularProgress,
  Tooltip as MuiTooltip,
  MenuItem,
} from "@material-ui/core";
import ModernSelect from "../../components/ModernSelect";
import api from "../../services/api";
import moment from "moment";

const useStyles = makeStyles((theme) => ({
  container: {
    width: "100%",
    overflowX: "auto",
  },
  grid: {
    display: "grid",
    gap: 3,
    width: "fit-content",
    minWidth: "100%",
  },
  cell: {
    borderRadius: 2,
    cursor: "pointer",
    transition: "all 0.15s ease",
    "&:hover": {
      transform: "scale(1.3)",
      zIndex: 2,
      boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
    },
  },
  dayLabel: {
    fontSize: 11,
    fontWeight: 500,
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    paddingRight: 8,
    userSelect: "none",
    [theme.breakpoints.down('sm')]: {
      fontSize: 8,
      paddingRight: 4,
    },
  },
  hourLabel: {
    fontSize: 10,
    textAlign: "center",
    userSelect: "none",
    [theme.breakpoints.down('sm')]: {
      fontSize: 7,
    },
  },
  legendBar: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    marginTop: 8,
  },
  legendGradient: {
    height: 8,
    width: 100,
    borderRadius: 4,
  },
  legendText: {
    fontSize: 10,
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 8,
    flexWrap: "wrap",
    gap: 6,
  },
  periodSelect: {
    minWidth: 146,
    "& .MuiOutlinedInput-root": {
      minHeight: 34,
      borderRadius: 999,
    },
    "& .MuiSelect-select": {
      padding: "7px 30px 7px 12px",
      fontSize: 11,
      fontWeight: 600,
    },
  },
}));

const DAYS = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
const HOURS = Array.from({ length: 24 }, (_, i) => i);

const PERIODS = [
  { value: 7, label: "Últimos 7 dias" },
  { value: 14, label: "Últimos 14 dias" },
  { value: 30, label: "Últimos 30 dias" },
  { value: 90, label: "Últimos 90 dias" },
];

function getColor(value, max, isDark) {
  if (!max || value === 0) {
    return isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)";
  }

  const intensity = Math.min(value / max, 1);

  // Degradê azul/indigo inspirado na Zenvia
  if (isDark) {
    const r = Math.round(30 + intensity * 50);
    const g = Math.round(40 + intensity * 80);
    const b = Math.round(80 + intensity * 175);
    const a = 0.3 + intensity * 0.7;
    return `rgba(${r}, ${g}, ${b}, ${a})`;
  }
  // Light mode
  const r = Math.round(230 - intensity * 170);
  const g = Math.round(235 - intensity * 155);
  const b = Math.round(255 - intensity * 30);
  return `rgb(${r}, ${g}, ${b})`;
}

const ChartsHeatmap = ({ companyId, groupName }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isDark = theme.palette.type === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(7);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const finalDate = moment().format("YYYY-MM-DD");
        const initialDate = moment()
          .subtract(period, "days")
          .format("YYYY-MM-DD");
        const params = { initialDate, finalDate };
        if (groupName) params.groupName = groupName;
        const { data: response } = await api.get("/dashboard/messageHeatmap", {
          params,
        });
        setData(response.data || []);
      } catch (err) {
        console.error("[ChartsHeatmap] Erro:", err);
        setData([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [period, companyId, groupName]);

  const { grid, maxCount } = useMemo(() => {
    // Monta matriz 7 dias x 24 horas
    const matrix = {};
    let max = 0;
    for (const row of data) {
      const key = `${row.day}-${row.hour}`;
      const count = Number(row.count) || 0;
      matrix[key] = count;
      if (count > max) max = count;
    }
    return { grid: matrix, maxCount: max };
  }, [data]);

  const cellHeight = isMobile ? 14 : 22;
  const labelWidth = isMobile ? 24 : 36;
  const cellGap = isMobile ? 1 : 2;

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight={240}
      >
        <CircularProgress size={32} />
      </Box>
    );
  }

  return (
    <Box className={classes.container}>
      <Box className={classes.header}>
        <Box display="flex" alignItems="center" style={{ gap: 4 }}>
          <Typography
            variant="subtitle2"
            style={{
              fontWeight: 600,
              color: isDark ? "#e2e8f0" : "#1e293b",
              fontSize: 12,
            }}
          >
            Tráfego de mensagens por horário
          </Typography>
          <MuiTooltip
            title="Mapa de calor com o total de mensagens trocadas (recebidas + enviadas) em cada hora do dia × dia da semana, somado no período selecionado neste card (Últimos 7 dias / 30 dias / 90 dias). Exclui tickets de grupo. Quanto mais escura a célula, maior o volume naquele intervalo."
            arrow
            placement="top"
            interactive
          >
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 14,
                height: 14,
                borderRadius: "50%",
                border: `1px solid ${isDark ? "rgba(148,163,184,0.3)" : "rgba(148,163,184,0.45)"}`,
                color: isDark ? "#4b5563" : "#94a3b8",
                fontSize: 8,
                fontWeight: 700,
                cursor: "help",
                flexShrink: 0
              }}
            >
              ?
            </span>
          </MuiTooltip>
        </Box>
        <ModernSelect
          className={classes.periodSelect}
          value={period}
          onChange={(e) => setPeriod(e.target.value)}
          formControlProps={{ size: "small" }}
        >
            {PERIODS.map((p) => (
              <MenuItem key={p.value} value={p.value}>
                {p.label}
              </MenuItem>
            ))}
        </ModernSelect>
      </Box>

      <Typography
        variant="caption"
        style={{
          color: isDark ? "#94a3b8" : "#64748b",
          display: "block",
          marginBottom: 6,
          fontSize: 10,
        }}
      >
        Média de mensagens por hora × dia da semana
      </Typography>

      {/* Heatmap grid — CSS grid auto-fills horizontally */}
      <Box
        style={{
          display: "grid",
          gridTemplateColumns: `${labelWidth}px repeat(24, 1fr)`,
          gap: cellGap,
          alignItems: "center",
        }}
      >
        {/* Corner spacer */}
        <Box />
        {/* Hour labels */}
        {HOURS.map((h) => (
          <Box
            key={h}
            className={classes.hourLabel}
            style={{ color: isDark ? "#64748b" : "#64748b" }}
          >
            {h % 3 === 0 ? `${String(h).padStart(2, "0")}` : ""}
          </Box>
        ))}
        {/* Day rows */}
        {DAYS.map((day, dayIndex) => (
          <React.Fragment key={day}>
            <Box
              className={classes.dayLabel}
              style={{ color: isDark ? "#94a3b8" : "#475569" }}
            >
              {day}
            </Box>
            {HOURS.map((hour) => {
              const count = grid[`${dayIndex}-${hour}`] || 0;
              return (
                <MuiTooltip
                  key={hour}
                  title={`${day} ${String(hour).padStart(2, "0")}:00 — ${count} mensagens`}
                  arrow
                  placement="top"
                >
                  <Box
                    className={classes.cell}
                    style={{
                      height: cellHeight,
                      backgroundColor: getColor(count, maxCount, isDark),
                    }}
                  />
                </MuiTooltip>
              );
            })}
          </React.Fragment>
        ))}
      </Box>

      {/* Legend */}
      <Box className={classes.legendBar}>
        <Typography
          className={classes.legendText}
          style={{ color: isDark ? "#64748b" : "#64748b" }}
        >
          0
        </Typography>
        <Box
          className={classes.legendGradient}
          style={{
            background: isDark
              ? "linear-gradient(to right, rgba(30,40,80,0.3), rgba(80,120,255,1))"
              : "linear-gradient(to right, rgb(230,235,255), rgb(60,80,225))",
          }}
        />
        <Typography
          className={classes.legendText}
          style={{ color: isDark ? "#64748b" : "#64748b" }}
        >
          {maxCount}
        </Typography>
      </Box>
    </Box>
  );
};

export default ChartsHeatmap;
